/** @jsx jsx */

import React, {
  useContext,
  useRef,
  useLayoutEffect,
  Fragment,
  useEffect,
} from 'react';
import { css, jsx } from '@emotion/core';
import { colors, getButtonFocus } from 'styles/variables';
import { interactionContext as _interactionContext } from './interactionContext';
import { MultichoiceContext } from './Multichoice_v2';
import isIE from 'services/isIE';
import isEDGE from 'services/isEDGE';
import isFirefox from 'services/isFirefox';
import { getMultichoiceData } from 'services/scorm/scormService';

const { lPink, lGrey0, lTeal0 } = colors;

const Radio = props => {
  let {
    id,
    selected,
    updateSelected,
    correct,
    resolved,
    checkboxColor,
    style,
    isMulti,
    isSquare,
  } = props;

  // contexts
  const interactionContext: any = useContext(_interactionContext);
  const { showCorrectAnswer } = interactionContext.props;

  let savedMultichoiceData = getMultichoiceData();

  let lockedBySCORM;
  if (savedMultichoiceData && savedMultichoiceData[interactionContext.id]) {
    savedMultichoiceData = savedMultichoiceData[interactionContext.id];
    //
    selected = savedMultichoiceData.selected.includes(id);
    resolved = true;
    lockedBySCORM = true;
  }

  return (
    <Fragment>
      <div
        css={css`
          all: unset;
          position: relative;
          flex: 0 0 auto;
          width: 20px;
          height: 20px;
          ${!isMulti && !isSquare && 'border-radius: 50%;'}
          border: 1px solid ${checkboxColor};
          outline: ${checkboxColor};
          background: ${selected ? checkboxColor : 'transparent'};
          ${!!showCorrectAnswer &&
            resolved &&
            correct &&
            'background: transparent;'}
          cursor: pointer;

          &:hover {
            opacity: 0.8;
            background: ${selected ? checkboxColor : 'transparent'};
          }

          &:focus {
border: 10px solid red;
display: none;
          }

          ${(isIE() || lockedBySCORM) && 'pointer-events: none;'}
        `}
        style={style}
        onClick={
          window.dev()
            ? e => {
                e.stopPropagation();
                updateSelected(id);
              }
            : null
        }
      >
        {!!showCorrectAnswer && resolved && correct && (
          <div
            css={css`
              display: flex;
              justify-content: center;
              align-items: center;
              width: 100%;
              height: 100%;
            `}
          >
            <Tick
              type={isMulti || isSquare ? 'checkbox' : 'radio'}
              color={checkboxColor}
            />
          </div>
        )}
      </div>
    </Fragment>
  );
};

const NewTick = ({ width = 9, height = 7, color }) => {
  // updated again. . .
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="10"
      height="10"
      viewBox="0 0 8 8"
    >
      <g>
        <path
          style={{
            stroke: color,
            strokeWidth: 1.5,
            strokeMiterlimit: 10,
            fill: 'none',
          }}
          d="M.5,4.46l2.41,2L7.44,1.55"
        />
      </g>
    </svg>
  );

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      xmlnsXlink="http://www.w3.org/1999/xlink"
      width={`${width}px`}
      height={`${height}px`}
      viewBox={`0 0 ${width} ${height}`}
      version="1.1"
    >
      <g
        style={{
          stroke: 'none',
          strokeWidth: 1,
          fill: 'none',
          fillRule: 'evenodd',
        }}
      >
        <g
          transform="translate(-1210.000000, -421.000000)"
          style={{
            stroke: color,
            strokeWidth: 1.5,
          }}
        >
          <path d="M1211,424.942296 C1211,424.942296 1211.80982,425.628198 1213.42945,427 L1218,422" />
        </g>
      </g>
    </svg>
  );
};

const Tick = ({ type = 'radio', width = 11, height = 11, color }) => {
  return (
    <div
      css={css({
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: 20,
        height: 20,
      })}
    >
      <NewTick color={color} />
    </div>
  );

  return (
    <div
      css={css`
    ${isIE() &&
      `
position: relative;
top: -${height / 3}px;
    `}
      width: ${width}px;
      height: ${height}px;
    `}
    >
      <svg>
        <polyline
          points={`0,${height / 2} ${width / 2},${height} ${width},0`}
          style={{
            fill: 'none',
            stroke: color,
            strokeWidth: 1,
          }}
        />
      </svg>
    </div>
  );
};

const Answer = props => {
  const { id, children, style = {} } = props;

  // contexts
  const interactionContext = useContext(_interactionContext);
  const multichoiceContext = useContext(MultichoiceContext);

  // refs
  const answer = useRef(null);
  const label = useRef(null);
  const optionText = useRef(null);

  const {
    panelBackground,
    panelBorderRadius = 0,
    optionTextColor,
    checkboxColor,
  } = interactionContext.props;

  let {
    selected,
    correct,
    updateSelected,
    submit,
    isMultiSelect,
    answerCount,
  } = multichoiceContext;

  // console.log({ updateSelected });

  correct = (correct as any).includes(id);
  const resolved = submit === 2;

  useLayoutEffect(() => {
    // remove the chameleon-text class
    // so only responds to local color
    const chameleonText = optionText.current.querySelector('.chameleon-text');
    chameleonText && chameleonText.classList.remove('chameleon-text');

    // if (isIE()) {
    //   answer.current.style.height = `${answer.current.scrollHeight}px`;
    // }
  });

  useEffect(() => {
    if (!isIE()) {
      return;
    }

    const checkAnswerHeight = () => {
      answer.current.style.height = `${answer.current.scrollHeight}px`;
    };

    const poll = setInterval(() => {
      const answerHeight = answer.current.scrollHeight;
      if (answerHeight) {
        // console.log('in IE11. answerHeight :', answerHeight);
        answer.current.style.height = `${answerHeight}px`;
        clearInterval(poll);
      }
    }, 1000 / 60);

    window.addEventListener('resize', checkAnswerHeight);

    return () => {
      window.removeEventListener('resize', checkAnswerHeight);
    };
  }, []);

  //
  //
  return (
    <div
      ref={answer}
      css={css`
        position: relative;
        display: flex;
        justify-content: space-between;
        align-items: center;
        width: 100%;
        min-height: 60px;
        margin: 7.5px 0;
        padding: 8px 15px;
        border: none;
        outline: none;
        border-radius: ${panelBorderRadius}px;
        text-align: left;
        background: ${panelBackground};
        color: ${optionTextColor};
        ${window.dev() && correct && `box-shadow: 0 0 0 2px ${lPink};`}
        cursor: pointer;
        pointer-events: ${submit === 0 ? 'auto' : 'none'};

        &:focus {
          ${getButtonFocus(panelBackground)}
        }

        ${resolved && 'pointer-events: none;'}
      `}
      onKeyUp={
        window.dev()
          ? null
          : (e: any) => {
              if ([' ', 'Enter'].includes(e.key)) {
                updateSelected(id);
              }
            }
      }
      onClick={
        window.dev()
          ? null
          : (e: any) => {
              // if (isEDGE() || isFirefox()) {
              //   return;
              // }
              updateSelected(id);
            }
      }
      tabIndex={0}
    >
      {!window.dev() && (
        <input
          id={`i${id}`}
          type={isMultiSelect ? 'checkbox' : 'radio'}
          value={`i${id}`}
          name={interactionContext.props.id}
          checked={(selected as any).includes(id)}
          readOnly
          css={css`
            position: absolute;
            left: 0;
            top: 0;
            opacity: 0;
          `}
          tabIndex={-1}
        />
      )}

      <label
        htmlFor={`i${id}`}
        ref={label}
        css={css`
          display: flex;
          flex: 1;

          ${!isIE() &&
            !isEDGE() &&
            `* {
        overflow-wrap: break-word;
        word-break: break-all;
        word-break: break-word;
        // unsetting this little bugger. be browser default
        // hyphens: auto;
      }`}

          * div {
            color: ${optionTextColor};
          }

          * p {
            margin: 0px;
          }

          ${!window.dev() && 'pointer-events: none;'}
        `}
        tabIndex={-1}
      >
        <div
          ref={optionText}
          css={css`
            width: 100%;
            padding-right: 15px;
          `}
        >
          {children}
        </div>
      </label>
      <Radio
        id={id}
        isMulti={isMultiSelect}
        isSquare={answerCount === 1}
        correct={correct}
        selected={(selected as any).includes(id)}
        updateSelected={updateSelected}
        resolved={resolved}
        checkboxColor={checkboxColor}
        style={style}
      />
    </div>
  );

  return (
    <button
      css={css`
        display: block;
        position: relative;
        width: 100%;
        margin: 7.5px 0;
        padding: 0;
        border: none;
        outline: none;
        text-align: left;

        &:focus {
          ${getButtonFocus(panelBackground)}
        }

        ${resolved && 'pointer-events: none;'}
      `}
      onKeyUp={
        window.dev() || (!isEDGE() && !isFirefox())
          ? null
          : e => {
              // console.log(e.key);
              if ([' ', 'Enter'].includes(e.key)) {
                updateSelected(id);
              }
            }
      }
      onClick={
        window.dev()
          ? null
          : (e: any) => {
              if (isEDGE() || isFirefox()) {
                return;
              }
              updateSelected(id);
            }
      }
    >
      <label
        ref={label}
        htmlFor={id}
        css={css`
          position: relative;
          display: flex;
          align-items: center;

          min-height: 60px;

          width: 100%;
          // margin: 7.5px 0;
          padding: 8px 15px;
          background: ${panelBackground};
          color: ${optionTextColor};

          ${window.dev() && correct && `box-shadow: 0 0 0 2px ${lPink};`}

          transition: all 0.2s;

          ${!isIE() &&
            !isEDGE() &&
            `* {
          overflow-wrap: break-word;
          word-break: break-all;
          word-break: break-word;
          hyphens: auto;
        }`}

          * div {
            color: ${optionTextColor};
          }

          * p {
            margin: 0px;
          }

          pointer-events: ${submit === 0 ? 'auto' : 'none'};
        `}
        tabIndex={-1}
      >
        <div
          ref={optionText}
          css={css`
            width: 100%;
            padding-right: 15px;
          `}
        >
          {children}
        </div>
        <Radio
          id={id}
          isMulti={isMultiSelect}
          correct={correct}
          selected={(selected as any).includes(id)}
          updateSelected={updateSelected}
          resolved={resolved}
          checkboxColor={checkboxColor}
          style={style}
        />
      </label>
    </button>
  );
};

export default Answer;
